import { Injectable } from '@angular/core';
import * as semaphore from 'semaphore';
import { OfflineStorageTimestampService } from '../offline-storage-timestamp.service';
import SemaphoreHelper from '../../helpers/semaphore-helper';
import { CollectionHelper } from '../../helpers/collection-helper';
import { Observable } from 'rxjs';
import { OfflineAsset } from './offline-asset';
import RegisteredAction from 'src/app/model/registered-action';
import * as moment from 'moment-timezone';
import { NgForage } from 'ngforage';
import AssetHelper from 'src/app/helpers/asset-helper';

@Injectable({
  providedIn: 'root',
})
export class PlacerSubProjectChangesService {
  static readonly storageKeyPrefix = 'subProject-';
  static readonly timestampCollectionKey = 'subProjects';
  static readonly offlineStorageMutex = semaphore(1);

  private expiredSubProjectRemover: ExpiredSubProjectRemover;

  constructor(
    private offlineStorageTimestampService: OfflineStorageTimestampService,
    private ngForage: NgForage
  ) {
    this.expiredSubProjectRemover = new ExpiredSubProjectRemover(
      this.offlineStorageTimestampService,
      this.ngForage
    );
    this.expiredSubProjectRemover.removeExpiredItems();
  }

  static getSubProjectStorageKey(id: number) {
    if (id == null) {
      throw new Error('id must not be null');
    }
    return this.storageKeyPrefix + id;
  }

  public getOfflineSubProjectChangeObject(
    id: number
  ): Observable<OfflineSubProjectChangeObject> {
    const stringified = localStorage.getItem(
      PlacerSubProjectChangesService.getSubProjectStorageKey(id)
    );
    if (stringified != null) {
      SemaphoreHelper.use(
        PlacerSubProjectChangesService.offlineStorageMutex,
        async () => {
          this.offlineStorageTimestampService.setTimestamps(
            PlacerSubProjectChangesService.timestampCollectionKey,
            [{ key: id.toString(), timestamp: Date.now() }]
          );
        }
      );
    }
    return new Observable((subscriber) => {
      const parsed: OfflineSubProjectChangeObject =
        stringified == null ? null : JSON.parse(stringified);
      const registeredActions =
        parsed == null ? null : parsed.registeredActions;
      if (CollectionHelper.isPopulated(registeredActions)) {
        registeredActions.forEach(
          (r) =>
            (r.actualDate =
              r.date == null ? null : moment.tz(r.date, 'Europe/Brussels'))
        );
      }
      subscriber.next(parsed);
    });
  }

  public getDirectOfflineSubProjectChangeObject(
    id: number
  ): OfflineSubProjectChangeObject {
    const stringified = localStorage.getItem(
      PlacerSubProjectChangesService.getSubProjectStorageKey(id)
    );

    if (stringified != null) {
      SemaphoreHelper.use(
        PlacerSubProjectChangesService.offlineStorageMutex,
        async () => {
          this.offlineStorageTimestampService.setTimestamps(
            PlacerSubProjectChangesService.timestampCollectionKey,
            [{ key: id.toString(), timestamp: Date.now() }]
          );
        }
      );
    }

    const parsed: OfflineSubProjectChangeObject =
      stringified == null ? null : JSON.parse(stringified);
    const registeredActions = parsed == null ? null : parsed.registeredActions;
    if (CollectionHelper.isPopulated(registeredActions)) {
      registeredActions.forEach(
        (r) =>
          (r.actualDate =
            r.date == null ? null : moment.tz(r.date, 'Europe/Brussels'))
      );
    }
    return parsed;
  }

  public async storeOfflineSubProjectChangeObject(
    offlineSubProjectChangeObject: OfflineSubProjectChangeObject
  ): Promise<void> {
    if (
      offlineSubProjectChangeObject == null ||
      offlineSubProjectChangeObject.id == null
    ) {
      throw new Error(
        'offlineSubProjectChangeObject must not be null and id must not be null'
      );
    }
    await SemaphoreHelper.use(
      PlacerSubProjectChangesService.offlineStorageMutex,
      async () => {
        localStorage.setItem(
          PlacerSubProjectChangesService.getSubProjectStorageKey(
            offlineSubProjectChangeObject.id
          ),
          JSON.stringify(offlineSubProjectChangeObject)
        );
        this.offlineStorageTimestampService.setTimestamps(
          PlacerSubProjectChangesService.timestampCollectionKey,
          [
            {
              key: offlineSubProjectChangeObject.id.toString(),
              timestamp: Date.now(),
            },
          ]
        );
      }
    );
  }

  public removeOfflineSubProjectChangeObject(
    offlineSubProjectChangeObject: OfflineSubProjectChangeObject
  ): void {
    if (offlineSubProjectChangeObject == null) {
      return;
    }
    SemaphoreHelper.use(
      PlacerSubProjectChangesService.offlineStorageMutex,
      async () => {
        const storageKey =
          PlacerSubProjectChangesService.getSubProjectStorageKey(
            offlineSubProjectChangeObject.id
          );
        const toRemove = localStorage.getItem(storageKey);
        if (toRemove != null) {
          PlacerSubProjectChangesService.cleanupSubProjectChangeObject(
            JSON.parse(toRemove),
            this.ngForage
          );
        }
        localStorage.removeItem(storageKey);
        this.offlineStorageTimestampService.removeItems(
          PlacerSubProjectChangesService.timestampCollectionKey,
          [offlineSubProjectChangeObject.id.toString()]
        );
      }
    );
  }

  public static async cleanupSubProjectChangeObject(
    offlineSubProjectChangeObject: OfflineSubProjectChangeObject,
    ngForage: NgForage
  ) {
    if (
      offlineSubProjectChangeObject != null &&
      CollectionHelper.isPopulated(offlineSubProjectChangeObject.offlineAssets)
    ) {
      for (const offlineAsset of offlineSubProjectChangeObject.offlineAssets) {
        await ngForage.removeItem(
          AssetHelper.getStorageKeyFor(offlineAsset.localFileUuid)
        );
      }
    }
  }
}

export interface OfflineSubProjectChangeObject {
  id: number;
  projectId: number;
  placerFeedback?: string;
  registeredActions?: RegisteredAction[];
  placementDone?: boolean;
  currentExecutionDate?: Date;
  offlineAssets?: OfflineAsset[];
}

class ExpiredSubProjectRemover {
  private readonly expiredItemRemovalInterval = 1800000;

  constructor(
    private offlineStorageTimestampService: OfflineStorageTimestampService,
    private ngForage: NgForage
  ) {}

  removeExpiredItems() {
    SemaphoreHelper.use(
      PlacerSubProjectChangesService.offlineStorageMutex,
      async () => {
        const expiredItems =
          this.offlineStorageTimestampService.getExpiredItems(
            PlacerSubProjectChangesService.timestampCollectionKey
          );
        if (CollectionHelper.isPopulated(expiredItems)) {
          expiredItems.forEach((i) => {
            const storageKey =
              PlacerSubProjectChangesService.getSubProjectStorageKey(
                Number(i.key)
              );
            const expiredItem = localStorage.getItem(storageKey);
            if (expiredItem != null) {
              PlacerSubProjectChangesService.cleanupSubProjectChangeObject(
                JSON.parse(expiredItem),
                this.ngForage
              );
            }
            localStorage.removeItem(storageKey);
          });
          this.offlineStorageTimestampService.removeItems(
            PlacerSubProjectChangesService.timestampCollectionKey,
            expiredItems.map((i) => i.key)
          );
        }
        setTimeout(
          () => this.removeExpiredItems(),
          this.expiredItemRemovalInterval
        );
      }
    );
  }
}
